import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ShieldCheck } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("password"); // "password" | "totp"
  const [ticket, setTicket] = useState("");
  const [code, setCode] = useState("");
  const { login, verify2fa } = useAuth();
  const navigate = useNavigate();

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const data = await login(email, password);
      if (data?.requires_totp) {
        setTicket(data.ticket);
        setStep("totp");
      } else {
        navigate("/admin");
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await verify2fa(ticket, code);
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "Invalid 2FA code");
    } finally {
      setLoading(false);
    }
  };

  const isTotp = step === "totp";

  return (
    <div className="min-h-screen bg-[#050A07] flex items-center justify-center px-6">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <a href="/" className="inline-block mb-6" aria-label="Delta GreenX home">
            <BrandLogo as="div" size="md" />
          </a>
          <div className="w-12 h-12 rounded-full bg-[#16A34A]/15 flex items-center justify-center mx-auto mb-4">
            {isTotp ? <ShieldCheck className="w-5 h-5 text-[#22C55E]" /> : <Lock className="w-5 h-5 text-[#22C55E]" />}
          </div>
          <h1 className="font-['Outfit'] text-2xl font-bold text-white" data-testid="admin-login-title">
            {isTotp ? "Two-Factor Authentication" : "Admin Access"}
          </h1>
          <p className="text-[#A1B2A8] text-sm mt-2">
            {isTotp ? "Enter the 6-digit code from your authenticator app" : "Sign in to manage applications"}
          </p>
        </div>

        {!isTotp && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4" data-testid="admin-login-form">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3" data-testid="login-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#A1B2A8] text-sm">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-[#09120D] border-[#1A2E22] text-white placeholder:text-[#A1B2A8]/40 focus:border-[#22C55E]"
                placeholder="hello@deltagreenx.com"
                data-testid="admin-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#A1B2A8] text-sm">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-[#09120D] border-[#1A2E22] text-white placeholder:text-[#A1B2A8]/40 focus:border-[#22C55E]"
                placeholder="Enter password"
                data-testid="admin-password-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#16A34A] text-white hover:bg-[#15803D] transition-all px-8 py-3 font-medium font-['Outfit'] disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>
        )}

        {isTotp && (
          <form onSubmit={handle2FASubmit} className="space-y-4" data-testid="admin-2fa-form">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3" data-testid="login-2fa-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="otp" className="text-[#A1B2A8] text-sm">6-digit code</Label>
              <Input
                id="otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]*"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                required
                autoFocus
                className="bg-[#09120D] border-[#1A2E22] text-white placeholder:text-[#A1B2A8]/40 focus:border-[#22C55E] tracking-[0.5em] text-center text-lg"
                placeholder="000000"
                data-testid="admin-2fa-input"
              />
            </div>
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="w-full bg-[#16A34A] text-white hover:bg-[#15803D] transition-all px-8 py-3 font-medium font-['Outfit'] disabled:opacity-50"
              data-testid="admin-2fa-submit-btn"
            >
              {loading ? "Verifying..." : "Verify & Sign In"}
            </button>
            <button
              type="button"
              onClick={() => { setStep("password"); setCode(""); setError(""); }}
              className="w-full text-[#A1B2A8] hover:text-white text-xs"
              data-testid="admin-2fa-back-btn"
            >
              ← Back to password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
